(() => {
  if (customElements.get('cart-accessories')) return;
  customElements.define('cart-accessories', class extends HTMLElement {
    connectedCallback() {
      this.drawer = this.closest('#CartDrawer');
      this.products = this.querySelector('[data-accessory-products]');
      this.status = this.querySelector('[role="status"]');
      this.root = window.Shopify?.routes?.root || '/';
      this.onClick = event => {
        const button = event.target.closest('[data-accessory-add]');
        if (button && this.contains(button)) this.addProduct(button);
      };
      this.onChange = event => {
        if (event.target.matches('select[data-accessory-variant]')) {
          const option = event.target.selectedOptions[0];
          if (option) event.target.closest('[data-accessory-card]').querySelector('[data-accessory-price]').textContent = option.dataset.price;
        }
      };
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      this.observer = new MutationObserver(() => this.loadProducts());
      if (this.drawer) this.observer.observe(this.drawer, {attributes: true, attributeFilter: ['class']});
      this.loadProducts();
    }

    async loadProducts() {
      if (this.loading || !this.drawer?.classList.contains('drawer--is-open') || this.drawer.classList.contains('is-empty')) return;
      if (this.loadedAt && Date.now() - this.loadedAt < 15000) {
        this.hidden = !this.products.querySelector('[data-accessory-card]');
        return;
      }
      this.loading = true;
      this.controller = new AbortController();
      const timeout = setTimeout(() => this.controller.abort(), 7000);
      try {
        const response = await fetch(this.dataset.configUrl, {signal: this.controller.signal, credentials: 'omit', cache: 'no-store'});
        if (!response.ok) throw new Error('Unavailable');
        const {settings} = await response.json();
        if (!settings || typeof settings.enabled !== 'boolean' || !Number.isInteger(settings.limit) || settings.limit < 1 || settings.limit > 12) throw new Error('Invalid config');
        if (!settings.enabled) {
          this.products.replaceChildren();
          this.hidden = true;
          this.loadedAt = Date.now();
          return;
        }
        const track = settings.mode === 'manual' ? await this.manualProducts(settings) : await this.collectionProducts(settings);
        if (!this.isConnected) return;
        this.querySelector('.cart-accessories__heading h3').textContent = settings.texts.heading;
        this.dataset.added = settings.texts.added;
        this.dataset.error = settings.texts.error;
        track.setAttribute('aria-label', settings.texts.heading);
        for (const card of track.querySelectorAll('[data-accessory-card]')) {
          const title = card.querySelector('.cart-accessories__name').textContent;
          const button = card.querySelector('[data-accessory-add]');
          button.querySelector('span').textContent = settings.texts.button;
          button.setAttribute('aria-label', settings.texts.button + ': ' + title);
          card.querySelector('select[data-accessory-variant]')?.setAttribute('aria-label', settings.texts.option + ': ' + title);
        }
        this.products.replaceChildren(track);
        this.status.hidden = true;
        this.hidden = !track.querySelector('[data-accessory-card]');
        this.loadedAt = Date.now();
      } catch {
        this.hidden = true;
      } finally {
        clearTimeout(timeout);
        this.loading = false;
      }
    }

    async collectionProducts(settings) {
      if (!/^[\p{L}\p{N}][\p{L}\p{N}-]{0,254}$/u.test(settings.collectionHandle) || !['created-descending', 'best-selling', 'manual'].includes(settings.sortBy)) throw new Error('Invalid collection');
      const query = new URLSearchParams({section_id: 'cart-accessories-products', sort_by: settings.sortBy, 'filter.v.availability': '1'});
      const response = await fetch(this.root + 'collections/' + encodeURIComponent(settings.collectionHandle) + '?' + query, {signal: this.controller.signal});
      if (!response.ok) throw new Error('Unavailable');
      const markup = new DOMParser().parseFromString(await response.text(), 'text/html');
      const track = markup.querySelector('[data-accessory-track]');
      if (!track) throw new Error('Missing products');
      [...track.querySelectorAll('[data-accessory-card]')].slice(settings.limit).forEach(card => card.remove());
      return track;
    }

    async manualProducts(settings) {
      if (!Array.isArray(settings.products) || settings.products.length > 12) throw new Error('Invalid products');
      const results = new Array(settings.products.length);
      let index = 0;
      // Bound concurrency while preserving the merchant's exact selected order.
      await Promise.all(Array.from({length: Math.min(4, settings.products.length)}, async () => {
        while (index < settings.products.length) {
          const i = index++, selection = settings.products[i];
          if (!/^[\p{L}\p{N}][\p{L}\p{N}-]{0,254}$/u.test(selection.handle)) continue;
          try {
            const response = await fetch(this.root + 'products/' + encodeURIComponent(selection.handle) + '.js', {signal: this.controller.signal});
            if (response.ok) results[i] = this.productCard(await response.json(), selection);
          } catch { /* Unpublished or unavailable products do not interrupt checkout. */ }
        }
      }));
      const track = document.createElement('ul');
      track.className = 'cart-accessories__track';
      track.dataset.accessoryTrack = '';
      track.tabIndex = 0;
      track.append(...results.filter(Boolean).slice(0, settings.limit));
      return track;
    }

    money(price) {
      if (window.theme?.Currency?.formatMoney) {
        const formatted = window.theme.Currency.formatMoney(price, this.dataset.moneyFormat);
        return new DOMParser().parseFromString(formatted, 'text/html').body.textContent;
      }
      return new Intl.NumberFormat(document.documentElement.lang || 'tr-TR', {style: 'currency', currency: this.dataset.currency || 'TRY'}).format(price / 100);
    }

    productCard(product, selection) {
      const variants = (product.variants || []).filter(v => v.available === true && /^\d+$/.test(String(v.id)) && Number.isFinite(Number(v.price)));
      if (!product.available || !variants.length) return null;
      const title = selection.title || product.title;
      const card = document.createElement('li');
      card.className = 'cart-accessories__card';
      card.dataset.accessoryCard = '';
      card.dataset.productId = String(product.id);
      const url = this.root + 'products/' + encodeURIComponent(selection.handle);
      const imageLink = document.createElement('a');
      imageLink.className = 'cart-accessories__image';
      imageLink.href = url;
      imageLink.tabIndex = -1;
      imageLink.setAttribute('aria-hidden', 'true');
      const source = product.featured_image?.src || product.featured_image;
      if (typeof source === 'string') {
        try {
          const imageUrl = new URL(source, location.origin);
          if (imageUrl.protocol === 'https:') {
            imageUrl.searchParams.set('width', '264');
            const image = document.createElement('img');
            image.src = imageUrl.href; image.alt = ''; image.loading = 'lazy'; image.width = 264; image.height = 264;
            imageLink.append(image);
          }
        } catch { /* An invalid image never removes the product's usable text link. */ }
      }
      const details = document.createElement('div'); details.className = 'cart-accessories__details';
      const name = document.createElement('a'); name.className = 'cart-accessories__name'; name.href = url; name.title = title; name.textContent = title;
      const price = document.createElement('span'); price.className = 'cart-accessories__price'; price.dataset.accessoryPrice = ''; price.textContent = this.money(variants[0].price);
      const single = product.variants.length === 1;
      const picker = document.createElement(single ? 'input' : 'select'); picker.dataset.accessoryVariant = '';
      if (single) {picker.type = 'hidden'; picker.value = String(variants[0].id);}
      else {
        picker.className = 'cart-accessories__variant';
        for (const variant of variants) {const option = document.createElement('option'); option.value = String(variant.id); option.textContent = variant.title; option.dataset.price = this.money(variant.price); picker.append(option);}
      }
      const button = document.createElement('button'); button.type = 'button'; button.className = 'cart-accessories__add'; button.dataset.accessoryAdd = ''; button.append(document.createElement('span'));
      details.append(name, price, picker); card.append(imageLink, details, button);
      return card;
    }

    async addProduct(button) {
      if (this.adding) return;
      const card = button.closest('[data-accessory-card]');
      const variantId = card.querySelector('[data-accessory-variant]')?.value;
      const productId = card.dataset.productId;
      if (!/^\d+$/.test(variantId || '')) return;
      this.adding = true;
      const originalLabel = button.querySelector('span').textContent;
      button.disabled = true; button.setAttribute('aria-busy', 'true'); button.querySelector('span').textContent = '…'; this.status.hidden = true;
      try {
        const response = await fetch(this.dataset.addUrl, {method: 'POST', headers: {'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest'}, body: JSON.stringify({items: [{id: variantId, quantity: 1, properties: {_ils_aov_source: 'cart_accessories_v1', ...(/^\d{1,20}$/.test(productId || '') ? {_ils_aov_product_id: productId} : {})}}]})});
        const result = await response.json();
        if (!response.ok || result.status || !result.items?.[0]) throw new Error(result.description || this.dataset.error);
        this.status.textContent = this.dataset.added; this.status.hidden = false;
        this.dispatchEvent(new CustomEvent('ajaxProduct:added', {detail: {product: result.items[0], addToCartBtn: button}, bubbles: true}));
      } catch (error) {
        this.status.textContent = error.message === 'Failed to fetch' ? this.dataset.error : error.message || this.dataset.error; this.status.hidden = false;
      } finally {
        this.adding = false; button.disabled = false; button.removeAttribute('aria-busy'); button.querySelector('span').textContent = originalLabel;
      }
    }

    disconnectedCallback() {
      this.controller?.abort(); this.observer?.disconnect(); this.removeEventListener('click', this.onClick); this.removeEventListener('change', this.onChange);
    }
  });
})();
